import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ResourceType } from "@common/types/auditLog";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { LogTimeCell } from "../../cells/LogTimeCell";
import { HistoryRuleGroupModifiedCell } from "../cells/HistoryRuleGroupModifiedCell";
import { HistoryRuleGroupDetailsColumn } from "./historyRuleGroupDetailsView";

export interface HistoryRuleGroupCreateDeleteDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    groupName: string;
}

export interface HistoryRuleGroupUpdateDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    groupName: string;
    changedFieldLabel: "name" | null;
    previous: string;
    modified: string;
}

const DEFAULT_PREVIOUS_LABEL = "Previous";
const DEFAULT_MODIFIED_LABEL = "Modified";

const getSingleFieldLabel = (
    data: HistoryRuleGroupUpdateDetailsRow[],
): "name" | null => {
    const fieldValues = data
        .map((row) => row.changedFieldLabel)
        .filter((field): field is "name" => field !== null);
    const uniqueFields = Array.from(new Set(fieldValues));

    if (uniqueFields.length !== 1) {
        return null;
    }

    return uniqueFields[0];
};

export const createHistoryRuleGroupCreateDeleteViewConfig = (): ViewConfig<
    HistoryRuleGroupCreateDeleteDetailsRow,
    HistoryRuleGroupDetailsColumn,
    never
> => ({
    columns: {
        [HistoryRuleGroupDetailsColumn.TIME]: {
            name: "Time",
            header: "",
            accessorKey: "logDate",
            cell: LogTimeCell,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryRuleGroupDetailsColumn.GROUP_NAME]: {
            name: "Group name",
            header: "Group name",
            accessorKey: "groupName",
            minSize: 260,
            maxSize: 400,
        },
        [HistoryRuleGroupDetailsColumn.PREVIOUS]: {
            name: "Previous",
            header: "Previous",
            accessorKey: "groupName",
            minSize: 280,
            maxSize: 420,
        },
        [HistoryRuleGroupDetailsColumn.MODIFIED]: {
            name: "Modified",
            header: "Modified",
            accessorKey: "groupName",
            minSize: 280,
            maxSize: 420,
        },
    },
});

export const createHistoryRuleGroupUpdateViewConfig = (
    data: HistoryRuleGroupUpdateDetailsRow[],
): ViewConfig<
    HistoryRuleGroupUpdateDetailsRow,
    HistoryRuleGroupDetailsColumn,
    never
> => {
    const singleField = getSingleFieldLabel(data);
    const previousLabel = singleField
        ? `Previous ${singleField}`
        : DEFAULT_PREVIOUS_LABEL;
    const modifiedLabel = singleField
        ? `Modified ${singleField}`
        : DEFAULT_MODIFIED_LABEL;

    return {
        columns: {
            [HistoryRuleGroupDetailsColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryRuleGroupDetailsColumn.GROUP_NAME]: {
                name: "Group name",
                header: "Group name",
                accessorKey: "groupName",
                minSize: 260,
                maxSize: 400,
            },
            [HistoryRuleGroupDetailsColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                minSize: 280,
                maxSize: 420,
            },
            [HistoryRuleGroupDetailsColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                cell: HistoryRuleGroupModifiedCell,
                minSize: 280,
                maxSize: 420,
            },
        },
    };
};
